import {inject, InjectionToken, LOCALE_ID} from "@angular/core";
import {autoserializeAs, inheritSerialization} from "dcerialize";
import type {Existing, IdentifiableModel} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import {ModificationDetails} from "../ModificationDetails";

export type LanguageCode = string;

export class CodeOnlyLanguage implements IdentifiableModel<LanguageCode> {
  @autoserializeAs(() => String)
  public code: LanguageCode;

  public get id(): LanguageCode {
    return this.code;
  }

  public set id(id: LanguageCode) {
    this.code = id;
  }

  public static existing(code: LanguageCode): Existing<CodeOnlyLanguage> {
    return new CodeOnlyLanguage(code);
  }

  public constructor(code: LanguageCode) {
    this.code = code;
  }
}

@inheritSerialization(() => CodeOnlyLanguage)
export class SimpleLanguage extends CodeOnlyLanguage {
  @autoserializeAs(() => String)
  public readonly name: string;
}

/**
 * @sealed
 */
@inheritSerialization(() => SimpleLanguage)
export class Language extends SimpleLanguage implements ModificationTrackedModel {
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}

/**
 * @sealed
 */
export class LanguageTranslation implements ModificationTrackedModel {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;

  public static createEmpty(): LanguageTranslation {
    const translation = new LanguageTranslation();
    translation.name = "";
    return translation;
  }
}

// Contrary to `LOCALE_ID` (which is e.g., en-GB for en), this token holds the language code as known in R-Force.
export const LANGUAGE_CODE = new InjectionToken<LanguageCode>("RF_LANGUAGE_CODE", {
  providedIn: "root",
  factory: (): string => {
    const locale = inject(LOCALE_ID);
    return locale.split("-")[0] ?? locale;
  },
});
